import React from "react";
import Box from "@mui/joy/Box";
import Tabs from "@mui/joy/Tabs";
import TabList from "@mui/joy/TabList";
import Tab, { tabClasses } from "@mui/joy/Tab";
import TabPanel from "@mui/joy/TabPanel";
import { Game } from "../../../models/games";
import { gamesListApi } from "../../games/gamesApi";
import { NotificationService } from "../../../common/notification";
import { useAnalyticsFilters } from "./hooks";
import { DemandDynamicsTab } from "./tabs/DemandDynamicsTab";
import { FunnelTab } from "./tabs/FunnelTab";
import { FillRateTab } from "./tabs/FillRateTab";
import { GamesSplitTab } from "./tabs/GamesSplitTab";
import { TopSkinsTab } from "./tabs/TopSkinsTab";
import { PriceSpreadTab } from "./tabs/PriceSpreadTab";
import { BookDepthTab } from "./tabs/BookDepthTab";
import { TopBuyersTab } from "./tabs/TopBuyersTab";
import { OrderConversionTab } from "./tabs/OrderConversionTab";
import { RisksTab } from "./tabs/RisksTab";
import { SaleChannelsTab } from "./tabs/SaleChannelsTab";

export const ANALYTICS_TAB_SLUGS = [
    "analytics-demand",
    "analytics-funnel",
    "analytics-fill-rate",
    "analytics-games",
    "analytics-top-skins",
    "analytics-price-spread",
    "analytics-book-depth",
    "analytics-top-buyers",
    "analytics-conversion",
    "analytics-sale-channels",
    "analytics-risks",
] as const;

const TAB_LABELS = [
    "Динамика",
    "Воронка",
    "Доля исполнения",
    "По играм",
    "Топ скинов",
    "Разница цен",
    "Открытый спрос",
    "Топ покупателей",
    "Конверсия",
    "Каналы продаж",
    "Риски",
] as const;

type Props = {
    isActive: boolean;
    currentTabIndex?: number;
    onTabChange?: (index: number) => void;
};

export function BidsAnalytics(props: Props) {
    const { isActive, currentTabIndex = 0, onTabChange } = props;
    const [tab, setTab] = React.useState(currentTabIndex);
    const [filters, setFilters] = useAnalyticsFilters();
    const [games, setGames] = React.useState<Game[]>([]);
    const [gamesLoading, setGamesLoading] = React.useState(true);

    React.useEffect(() => {
        setTab(currentTabIndex);
    }, [currentTabIndex]);

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const result = await gamesListApi();
                if (cancelled) {
                    return;
                }
                if (result.result) {
                    setGames(result.value ?? []);
                } else {
                    NotificationService.alertError(
                        "Не удалось загрузить список игр. Попробуйте позже."
                    );
                }
            } finally {
                if (!cancelled) {
                    setGamesLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const handleTabChange = (_: React.SyntheticEvent | null, value: number | string | null) => {
        const idx = typeof value === "number" ? value : 0;
        setTab(idx);
        onTabChange?.(idx);
    };

    const common = {
        filters,
        onFiltersChange: setFilters,
        games,
        gamesLoading,
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minHeight: 0, flex: 1 }}>
            <Tabs
                value={tab}
                onChange={handleTabChange}
                sx={{ bgcolor: "transparent", flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}
            >
                <TabList
                    tabFlex={1}
                    size="sm"
                    sx={{
                        justifyContent: "left",
                        overflowX: "auto",
                        flexWrap: "nowrap",
                        [`&& .${tabClasses.root}`]: {
                            fontWeight: "600",
                            flex: "initial",
                            color: "text.tertiary",
                            whiteSpace: "nowrap",
                            [`&.${tabClasses.selected}`]: {
                                bgcolor: "transparent",
                                color: "text.primary",
                                "&::after": {
                                    height: "2px",
                                    bgcolor: "primary.500",
                                },
                            },
                        },
                    }}
                >
                    {TAB_LABELS.map((label, index) => (
                        <Tab key={label} sx={{ borderRadius: "6px 6px 0 0" }} indicatorInset value={index}>
                            {label}
                        </Tab>
                    ))}
                </TabList>

                <TabPanel value={0} sx={{ px: 0, pt: 1.5, pb: 0 }}>
                    <DemandDynamicsTab isActive={isActive && tab === 0} {...common} />
                </TabPanel>
                <TabPanel value={1} sx={{ px: 0, pt: 1.5, pb: 0 }}>
                    <FunnelTab isActive={isActive && tab === 1} {...common} />
                </TabPanel>
                <TabPanel value={2} sx={{ px: 0, pt: 1.5, pb: 0 }}>
                    <FillRateTab isActive={isActive && tab === 2} {...common} />
                </TabPanel>
                <TabPanel value={3} sx={{ px: 0, pt: 1.5, pb: 0 }}>
                    <GamesSplitTab isActive={isActive && tab === 3} {...common} />
                </TabPanel>
                <TabPanel value={4} sx={{ px: 0, pt: 1.5, pb: 0 }}>
                    <TopSkinsTab isActive={isActive && tab === 4} {...common} />
                </TabPanel>
                <TabPanel value={5} sx={{ px: 0, pt: 1.5, pb: 0 }}>
                    <PriceSpreadTab isActive={isActive && tab === 5} {...common} />
                </TabPanel>
                <TabPanel value={6} sx={{ px: 0, pt: 1.5, pb: 0 }}>
                    <BookDepthTab isActive={isActive && tab === 6} {...common} />
                </TabPanel>
                <TabPanel value={7} sx={{ px: 0, pt: 1.5, pb: 0 }}>
                    <TopBuyersTab isActive={isActive && tab === 7} {...common} />
                </TabPanel>
                <TabPanel value={8} sx={{ px: 0, pt: 1.5, pb: 0 }}>
                    <OrderConversionTab isActive={isActive && tab === 8} {...common} />
                </TabPanel>
                <TabPanel value={9} sx={{ px: 0, pt: 1.5, pb: 0 }}>
                    <SaleChannelsTab isActive={isActive && tab === 9} {...common} />
                </TabPanel>
                <TabPanel value={10} sx={{ px: 0, pt: 1.5, pb: 0 }}>
                    <RisksTab isActive={isActive && tab === 10} {...common} />
                </TabPanel>
            </Tabs>
        </Box>
    );
}
